import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

/**
 * Generic error boundary.
 *
 * Used twice: once outside the Fluent provider with a dependency-free fallback
 * (so a broken theme cannot break the error screen), and once inside the router
 * with the styled `ErrorState`.
 */

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Receives the captured error plus a reset callback. */
  fallback: (error: Error, reset: () => void) => ReactNode;
  /** Changing this value clears the error (e.g. the current pathname). */
  resetKey?: string;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
  resetKey: string | undefined;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
    // Surfaced in the browser console; a real telemetry sink is a later phase.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private readonly reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (error) return this.props.fallback(error, this.reset);
    return this.props.children;
  }
}

/**
 * Dependency free fallback (no Fluent, no Tailwind classes that rely on the
 * theme being mounted) for the outermost boundary.
 */
export function RootErrorFallback({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}): JSX.Element {
  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        backgroundColor: '#f7f9fc',
        color: '#1b2030',
        fontFamily: "'Segoe UI Variable Text', 'Segoe UI', system-ui, sans-serif",
      }}
    >
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px' }}>
          La aplicación no pudo iniciarse
        </h1>
        <p style={{ fontSize: 14, color: '#5b6478', margin: '0 0 20px' }}>
          Ocurrió un error inesperado al cargar Salas de juntas. Vuelve a intentarlo; si el problema
          continúa, comparte este mensaje con el equipo de sistemas.
        </p>
        <pre
          style={{
            fontSize: 12,
            textAlign: 'left',
            background: '#eff2f7',
            border: '1px solid #e2e7ef',
            borderRadius: 8,
            padding: 12,
            overflowX: 'auto',
            margin: '0 0 20px',
          }}
        >
          {error.message}
        </pre>
        <button
          type="button"
          onClick={reset}
          style={{
            minHeight: 44,
            padding: '0 20px',
            borderRadius: 6,
            border: 'none',
            background: '#253d72',
            color: '#ffffff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Reintentar
        </button>
      </div>
    </div>
  );
}
